import ePub, { Rendition } from 'epubjs'

declare global {
  interface Window {
    initializeEpub: (element: HTMLElement) => void
    switchEpub: (element: HTMLElement) => void
    downloadFile: (element: HTMLElement) => void
  }
}

export type Section = {
  href: string
}

let rendition: Rendition | null = null
let currentElement: HTMLElement | null = null

function nextHandler(e: { preventDefault: () => void }) {
  rendition?.next()
  e.preventDefault()
}

function prevHandler(e: { preventDefault: () => void }) {
  rendition?.prev()
  e.preventDefault()
}

// Add keydown event listener for arrow key detection
document.addEventListener('keydown', function (event) {
  if (event.key === 'ArrowRight') {
    event.preventDefault() // Prevent default behavior of arrow key

    // Go to the next page
    rendition?.next()
  }
  if (event.key === 'ArrowLeft') {
    event.preventDefault() // Prevent default behavior of arrow key

    // Go to the next page
    rendition?.prev()
  }
})

export const initializeEpub = () => {
  const epubReader = document.querySelectorAll<HTMLElement>('.epub-reader')[0]
  if (!epubReader) return

  epubReader.style.display = 'block'
  const element = document.querySelectorAll<HTMLElement>('.sky-pilot-epub-viewer')[0]
  if (!element) return

  element.classList.add('loading')
  const url = element.getAttribute('data-epub-url')
  const orderItemId = element.getAttribute('data-item-id')
  const orderId = element.getAttribute('data-order-id')
  const fileId = element.getAttribute('data-file-id')
  const fileSize = element.getAttribute('data-file-size') || ''
  if (!url) return

  // warn if big file is about to be downloaded
  const numericValue = parseInt(fileSize, 10)
  const unit = fileSize.split(' ')[1]
  if ((unit === 'MB' && numericValue >= 20) || unit === 'GB') {
    if (!confirm('EBook size is over 20MB. Loading can be slow. Continue?')) {
      element.classList.remove('loading')
      epubReader.style.display = 'none'
      return
    }
  }

  const book = ePub(url)
  element.innerHTML = ''
  rendition = book.renderTo(element, {
    manager: 'continuous',
    flow: 'paginated',
    height: `${(window.innerHeight / 3) * 2}px`,
    width: `${window.innerWidth > 1100 ? 1100 : window.innerWidth - 20}px`,
  })

  rendition.on('relocated', function (location: { start: { cfi: string } }) {
    localStorage.setItem(orderItemId+'-'+fileId+'-savedPosition', location.start.cfi)
  })

  currentElement = element
  const savedPosition = localStorage.getItem(orderItemId+'-'+fileId+'-savedPosition')
  const displayed = rendition.display(savedPosition || undefined)

  displayed.then(function () {
    if (fileId && orderId && orderItemId) {
      recordEpubView(element, fileId, orderItemId, orderId)
    }
    element.classList.remove('loading')
  })

  Array.from(currentElement.parentElement?.getElementsByClassName('prev') || []).forEach(function (element) {
    element.addEventListener('click', prevHandler, false)
  })
  Array.from(currentElement.parentElement?.getElementsByClassName('next') || []).forEach(function (element) {
    element.addEventListener('click', nextHandler, false)
  })
}

window.initializeEpub = initializeEpub

export const switchEpub = (element: HTMLElement) => {
  // detach and reset current rendition if present
  if (rendition && currentElement) {
    Array.from(currentElement.parentElement?.getElementsByClassName('next') || []).forEach(function (element) {
      element?.removeEventListener('click', nextHandler)
    })
    Array.from(currentElement.parentElement?.getElementsByClassName('prev') || []).forEach(function (element) {
      element?.removeEventListener('click', prevHandler)
    })
    rendition.destroy()
    rendition = null
    currentElement = null
  }

  const url = element.getAttribute('data-epub-url') || ''
  const orderItemId = element.getAttribute('data-item-id') || ''
  const orderId = element.getAttribute('data-order-id') || ''
  const fileId = element.getAttribute('data-file-id') || ''
  const fileSize = element.getAttribute('data-file-size') || ''
  const downloadUrl = element.getAttribute('data-download-url') || ''

  const targetDiv = document.querySelectorAll<HTMLElement>('.sky-pilot-epub-viewer')[0]
  // Set the attributes on the target div element
  targetDiv.setAttribute('data-epub-url', url)
  targetDiv.setAttribute('data-item-id', orderItemId)
  targetDiv.setAttribute('data-order-id', orderId)
  targetDiv.setAttribute('data-file-id', fileId)
  targetDiv.setAttribute('data-file-size', fileSize)
  targetDiv.setAttribute('data-download-url', downloadUrl)

  // setup new rendition
  initializeEpub()
  // set as active link
  document.querySelectorAll<HTMLElement>('.sky-pilot-list-item').forEach((link) => link.classList.remove('active'))
  element.classList.add('active')
}

window.switchEpub = switchEpub

const recordEpubView = (element: HTMLElement, fileId: string, orderItemId: string, orderId: string) => {
  if (!element.getAttribute('data-viewed')) {
    fetch('/apps/downloads/epub_views/' + fileId, {
      method: 'post',
      body: JSON.stringify({ order_item_id: orderItemId, order_id: orderId }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
    element.setAttribute('data-viewed', 'true')
  }
}

const downloadFile = (element: HTMLElement) => {
  const fileUrl = element.parentElement?.parentElement?.getAttribute('data-download-url') || ''
  const link = document.createElement('a')
  link.href = fileUrl
  link.click()
}
window.downloadFile = downloadFile
